/**
 * Uber Menu — the live Uber Eats menu, laid out like Product Management.
 *
 * One row per Uber item: Image, SKU, Name, Category, Sub Category, Price,
 * Stock, Sellable. Category is the Uber menu (aisle) the item sits in and
 * Sub Category the Uber category inside it — the shape Sync App to Uber
 * builds (one menu per app category, one Uber category per tag). Stock is
 * the app's available quantity at the Uber store, the number that decides
 * whether the item is suspended; Sellable means not suspended on Uber.
 *
 * The Uber price is OURS to set here (Jason, 25 Oct 2026): the Uber Eats section owns it, it no
 * longer follows Tier 1. Click a price to change it: it is stored and put on the live Uber item
 * at once. When the Lightspeed (Tier 1) price has moved since the Uber price was set, the row says
 * so and offers the formula price (Tier 1 / markup, nearest 50c).
 *
 * Since 26 Oct 2026 this page IS the Uber catalogue: Uber's own products (name, description,
 * image, aisle, price, on/off), keyed by SKU and seeded once from the live menu ("Set up the Uber
 * catalogue"). Two green alerts sit above the cards: new Lightspeed products, and products whose
 * Lightspeed details no longer match. Both are settled in Uber Tools > Push Uber Menu.
 *
 * Hold (Jason, 26 Oct 2026): an item put on hold here is suspended on Uber with MANUAL_HOLD, and
 * nothing automated (stock webhook, 2am check, menu pushes) re-opens it; only releasing it here
 * does. A sold-out item (OUT_OF_STOCK) still re-opens by itself when Vend has stock again.
 *
 * Changed on Uber (Jason, 26 Oct 2026): an item whose name, description, image, price or aisle on
 * Uber no longer matches the catalogue (changed in Uber Manager, or a push that did not land) is
 * flagged. "Take Uber's" copies Uber's values into the catalogue; "Keep ours" puts ours back on
 * Uber now (an aisle goes back at the next push). A push would otherwise put ours back silently.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Grid, Typography, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Button, Stack, Select, MenuItem, FormControl,
  InputLabel, Paper, Pagination, alpha, Skeleton, Autocomplete, Alert, Switch,
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon, CloudDownload as SeedIcon,
  Storefront as StorefrontIcon,
  RemoveShoppingCart as OutOfStockIcon,
  ShoppingCart as InStockIcon,
  PauseCircle as SuspendedIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useAdminTheme, type AdminColorTokens, APP_BAR_HEIGHT } from '../admin-theme';
import { useScrollToListTop } from '../hooks/useScrollToListTop';
import authInstance from '../services/httpClient';
import Logger from '../utils/Logger';

// ============================================================================
// Types
// ============================================================================

/** GET /admin/tools/uber-menu/items — one row per live Uber item. */
interface UberMenuRow {
  id: string;
  productId: string | null;
  sku: string;
  name: string;
  category: string;
  subCategory: string;
  priceCents: number;
  imageUrl: string | null;
  /** App available stock at the Uber store; null when the SKU isn't in the catalogue. */
  stock: number | null;
  /** Stock as Uber holds it (max_permitted; sold out = 0; null = not on Uber) */
  uberStock?: number | null;
  uberStockCapped?: boolean;
  sellable: boolean;
  suspendedReason: string | null;
  /** The stored Uber price; null = none stored yet (the formula is used at the next push). */
  storedPriceCents: number | null;
  priceSource: string | null;
  tier1: number | null;
  tier1AtSet: number | null;
  appPriceChanged: boolean;
  suggestedCents: number | null;
  /** catalogue mode: 'active' | 'off' (left off Uber from the next push) */
  status?: string;
  /** catalogue mode: on the live Uber menu right now */
  onUber?: boolean;
  /** catalogue mode: Lightspeed fields that moved since the item was reconciled */
  mismatch?: string[];
  /** catalogue mode: on hold (suspended by a person) */
  held?: boolean;
  /** catalogue mode: what differs on Uber (changed there directly) */
  uberDiff?: Array<{ field: string; ours: string; uber: string }>;
}

/** On sale on Uber now: on the menu and not suspended (Uber's own state, not the app's stock). */
const onSaleOnUber = (r: UberMenuRow) => r.sellable && !isHeld(r);
/** Suspended on Uber as sold out (the sync's OUT_OF_STOCK), as opposed to held by a person. */
const soldOutOnUber = (r: UberMenuRow) => !r.sellable && r.suspendedReason === 'OUT_OF_STOCK';

const changedOnUber = (r: UberMenuRow) => (r.uberDiff?.length ?? 0) > 0;
const DIFF_LABEL: Record<string, string> = { name: 'name', description: 'description', image: 'image', price: 'price', aisle: 'aisle' };

/** Suspended by a person rather than sold out: only the Hold switch re-opens it. */
const isHeld = (r: UberMenuRow) => r.held ?? (!!r.suspendedReason && !['OUT_OF_STOCK', 'NOT_ON_UBER'].includes(r.suspendedReason));

interface UberMenuResponse {
  items: UberMenuRow[];
  categories: string[];
  subCategories: string[];
  cachedAt: string | null;
  /** 'catalogue' once the Uber catalogue is set up; 'live' = the live menu read straight */
  mode?: 'catalogue' | 'live';
  /** null while Lightspeed's first read is still running */
  alerts?: { newProducts: number; mismatching: number } | null;
  /** false off production: the review runs against the real Vend only */
  lightspeedChecks?: boolean;
}

// ============================================================================
// Constants / helpers
// ============================================================================

const PAGE_SIZE = 50;

const SOURCE_LABEL: Record<string, string> = { live: 'from Uber', formula: 'formula', manual: 'set by hand' };

/**
 * The Uber price: click to change (Enter or leaving the box saves, Esc cancels). Shows where
 * the stored price came from and, when Tier 1 moved since it was set, the new app price with
 * the formula suggestion and a one-click Use.
 */
function PriceCell({ row, colors, editable, priceLabel, onSave }: { row: UberMenuRow; colors: AdminColorTokens; editable: boolean; priceLabel: string; onSave: (cents: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const start = () => {
    if (!editable) return;
    setValue((row.priceCents / 100).toFixed(2));
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    const cents = Math.round(parseFloat(value.replace(/[^0-9.]/g, '')) * 100);
    if (Number.isFinite(cents) && cents > 0 && cents <= 140000 && cents !== row.priceCents) onSave(cents);
  };
  return (
    <Box>
      {editing ? (
        <TextField
          size="small"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
          inputProps={{ 'aria-label': `Uber price for ${row.sku}`, inputMode: 'decimal' }}
          sx={{ width: 110, '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary } }}
        />
      ) : (
        <Typography
          role="button"
          tabIndex={0}
          title={editable ? 'Click to change the Uber price' : 'Not in the app catalogue'}
          onClick={start}
          onKeyDown={(e) => { if (e.key === 'Enter') start(); }}
          sx={{
            fontSize: '0.85rem', color: colors.textPrimary, fontFamily: 'monospace',
            cursor: editable ? 'pointer' : 'default', '&:hover': editable ? { color: colors.info } : {},
          }}
        >
          {formatCurrency(row.priceCents)}
        </Typography>
      )}
      {row.storedPriceCents != null && row.storedPriceCents !== row.priceCents && (
        <Typography sx={{ fontSize: '0.68rem', color: colors.warning }}>stored {formatCurrency(row.storedPriceCents)} (next push)</Typography>
      )}
      {row.priceSource && (
        <Typography sx={{ fontSize: '0.68rem', color: colors.textMuted }}>{SOURCE_LABEL[row.priceSource] ?? row.priceSource}</Typography>
      )}
      {row.appPriceChanged && row.tier1 != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.68rem', color: colors.warning }}>
            {priceLabel} R{row.tier1AtSet?.toFixed(2)} to R{row.tier1.toFixed(2)}
            {row.suggestedCents ? ` · suggested ${formatCurrency(row.suggestedCents)}` : ''}
          </Typography>
          {row.suggestedCents ? (
            <Button size="small" onClick={() => onSave(row.suggestedCents as number)} sx={{ minWidth: 0, p: '0 4px', fontSize: '0.68rem', textTransform: 'none' }}>
              Use
            </Button>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

const formatCurrency = (cents: number) =>
  `R${(cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function getStockColor(available: number | null | undefined, colors: AdminColorTokens): string {
  if (available === undefined || available === null) return colors.textMuted;
  if (available <= 0) return colors.error;
  if (available <= 5) return colors.warning;
  return colors.success;
}

const headCellSx = (colors: AdminColorTokens) => ({
  color: colors.textMuted, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
  letterSpacing: '0.05em', borderColor: colors.border,
});

const selectSx = (colors: AdminColorTokens) => ({
  bgcolor: colors.bgElevated, color: colors.textPrimary,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
  '& .MuiSelect-icon': { color: colors.textSecondary },
});

// ============================================================================
// Small components (same look as Product Management)
// ============================================================================

function GlowCard({ icon, label, value, color, colors }: {
  icon: React.ReactElement; label: string; value: number; color: string; colors: AdminColorTokens;
}) {
  return (
    <Paper
      sx={{
        p: 2.5, height: '100%',
        bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 3,
        display: 'flex', alignItems: 'center', gap: 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', overflow: 'hidden',
        '&:hover': {
          bgcolor: colors.bgElevated, borderColor: alpha(color, 0.4),
          boxShadow: `0 0 30px ${alpha(color, 0.15)}, inset 0 0 30px ${alpha(color, 0.05)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{
        width: 52, height: 52, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(color, 0.12), color, flexShrink: 0, boxShadow: `0 0 15px ${alpha(color, 0.1)}`,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 26 } } as any)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 800, lineHeight: 1.2, mt: 0.25 }}>
          {value.toLocaleString()}
        </Typography>
      </Box>
    </Paper>
  );
}

function FilterAutocomplete({ label, placeholder, options, value, onChange, colors }: {
  label: string; placeholder: string; options: string[]; value: string;
  onChange: (value: string) => void; colors: AdminColorTokens;
}) {
  return (
    <Autocomplete
      freeSolo
      size="small"
      options={options}
      value={value || null}
      onChange={(_, v) => onChange(v ?? '')}
      onInputChange={(_, v, reason) => { if (reason === 'input' || reason === 'clear') onChange(v ?? ''); }}
      sx={{ minWidth: 180 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          sx={{
            bgcolor: colors.bgElevated,
            '& .MuiInputBase-input': { color: colors.textPrimary },
            '& .MuiInputLabel-root': { color: colors.textSecondary },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': { color: colors.textSecondary },
          }}
        />
      )}
    />
  );
}

function YesNoChip({ on, colors }: { on: boolean; colors: AdminColorTokens }) {
  return (
    <Chip
      label={on ? 'Yes' : 'No'}
      size="small"
      sx={{
        bgcolor: alpha(on ? colors.success : colors.textMuted, 0.12),
        color: on ? colors.success : colors.textMuted,
        fontSize: '0.65rem', height: 20,
      }}
    />
  );
}

// ============================================================================
// Page
// ============================================================================

const UberMenu: React.FC = () => {
  const { colors } = useAdminTheme();
  const notify = useNotify();

  const [rows, setRows] = useState<UberMenuRow[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [mode, setMode] = useState<'catalogue' | 'live'>('live');
  const [alerts, setAlerts] = useState<UberMenuResponse['alerts']>(null);
  const [lsChecks, setLsChecks] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subCategoryFilter, setSubCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [sellableFilter, setSellableFilter] = useState('');
  type YesNo = '' | 'yes' | 'no';
  const [sellableHdr, setSellableHdr] = useState<YesNo>('');
  const [holdHdr, setHoldHdr] = useState<YesNo>('');
  const [menuHdr, setMenuHdr] = useState<YesNo>('');
  const cycle = (v: YesNo): YesNo => (v === '' ? 'yes' : v === 'yes' ? 'no' : '');
  const [changedOnly, setChangedOnly] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [sortBy, setSortBy] = useState<'sku' | 'name' | 'category' | 'subCategory' | 'price' | 'stock'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const listTopRef = useScrollToListTop(page, { below: stickyHeaderRef });

  const fetchMenu = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await authInstance.get<UberMenuResponse>('admin/tools/uber-menu/items', { timeout: 90000 });
      setRows(data.items || []);
      setCategories(data.categories || []);
      setSubCategories(data.subCategories || []);
      setCachedAt(data.cachedAt);
      setMode(data.mode === 'catalogue' ? 'catalogue' : 'live');
      if (data.mode !== 'catalogue') { setSellableFilter((f) => (f === 'uber-changed' ? '' : f)); setMenuHdr(''); }
      setAlerts(data.alerts ?? null);
      setLsChecks(data.lightspeedChecks !== false);
    } catch (err: any) {
      Logger.error('Failed to load the Uber menu:', err);
      if (!quiet) notify(err?.response?.data?.error || 'Failed to load the Uber menu', { type: 'error' });
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [notify]);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  // The alerts need Lightspeed, read in the background (~30s the first time): ask again until they land.
  const [alertTries, setAlertTries] = useState(0);
  useEffect(() => {
    if (mode !== 'catalogue' || !lsChecks || alerts || loading || alertTries >= 6) return undefined;
    const t = setTimeout(() => { setAlertTries((n) => n + 1); fetchMenu({ quiet: true }); }, 15000);
    return () => clearTimeout(t);
  }, [mode, lsChecks, alerts, loading, alertTries, fetchMenu]);

  /** The menu is cached server-side for 6h; this drops the cache and re-reads Uber. */
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await authInstance.post('admin/tools/uber-menu/invalidate-cache', { target: 'uber' });
      await fetchMenu();
      notify('Uber menu refreshed', { type: 'success' });
    } catch (err: any) {
      notify('Failed to refresh the Uber menu', { type: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  /** Store an Uber price and put it on the live item (the catalogue's price, or before it exists PUT price-overrides). */
  const savePrice = async (row: UberMenuRow, cents: number) => {
    if (mode !== 'catalogue' && !row.productId) { notify(`${row.sku} is not in the app catalogue`, { type: 'warning' }); return; }
    try {
      const { data } = mode === 'catalogue'
        ? await authInstance.put(`admin/tools/uber-menu/catalogue/${encodeURIComponent(row.sku)}/price`, { priceCents: cents })
        : await authInstance.put(`admin/tools/uber-menu/price-overrides/${row.productId}`, { priceCents: cents });
      const live = data?.live;
      const liveError: string | undefined = live?.error || (live && live.onMenu === false ? 'not on the Uber menu' : undefined);
      setRows((rs) => rs.map((r) => (r.sku === row.sku && r.id === row.id ? {
        ...r,
        priceCents: liveError ? r.priceCents : cents,
        storedPriceCents: cents,
        priceSource: 'manual',
        tier1AtSet: r.tier1,
        appPriceChanged: false,
      } : r)));
      notify(liveError
        ? `Saved ${formatCurrency(cents)}; Uber did not take it yet (${liveError}), it goes with the next push`
        : live?.queued
          ? `${row.name}: ${formatCurrency(cents)} saved, showing on Uber in a minute or two`
          : `${row.name}: ${formatCurrency(cents)} saved`, { type: liveError ? 'warning' : 'success' });
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not save the price', { type: 'error' });
    }
  };

  /** Copy the live Uber menu in as the Uber catalogue (nothing changes on Uber). */
  const handleSeed = async () => {
    if (!window.confirm(`Copy all ${rows.length.toLocaleString()} items on the live Uber menu in as the Uber catalogue? Each keeps exactly the name, description, image, aisle and price it has on Uber now, and the aisle mappings take the live names and order. Nothing changes on Uber.`)) return;
    setSeeding(true);
    try {
      const { data } = await authInstance.post('admin/tools/uber-menu/catalogue/seed', {}, { timeout: 120000 });
      const d = data?.data || {};
      const skipped: string[] = d.skipped || [];
      notify(`Uber catalogue set up: ${d.saved ?? 0} of ${d.liveItems ?? 0} live items${skipped.length ? `, ${skipped.length} skipped (${skipped.slice(0, 3).join('; ')}${skipped.length > 3 ? '…' : ''})` : ''}${d.unmappedMenus?.length ? `. Aisles with no mapping: ${d.unmappedMenus.join(', ')}` : ''}`, { type: skipped.length || d.unmappedMenus?.length ? 'warning' : 'success', autoHideDuration: 12000 });
      await fetchMenu();
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not store the prices', { type: 'error' });
    } finally {
      setSeeding(false);
    }
  };

  // ── Filtering / sorting (client-side — the whole menu comes down in one call) ──

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cat = categoryFilter.trim().toLowerCase();
    const sub = subCategoryFilter.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (q && !(r.name.toLowerCase().includes(q) || r.sku.toLowerCase().includes(q))) return false;
      if (cat && !r.category.toLowerCase().includes(cat)) return false;
      if (sub && !r.subCategory.toLowerCase().includes(sub)) return false;
      if (stockFilter === 'in-stock' && !onSaleOnUber(r)) return false;
      if (stockFilter === 'out-of-stock' && !soldOutOnUber(r)) return false;
      if (stockFilter === 'low-stock' && !(((r.uberStock ?? r.stock) ?? 0) > 0 && ((r.uberStock ?? r.stock) ?? 0) <= 5)) return false;
      if (sellableFilter === 'true' && !r.sellable) return false;
      if (sellableFilter === 'false' && r.sellable) return false;
      if (sellableFilter === 'held' && !isHeld(r)) return false;
      if (sellableHdr && (sellableHdr === 'yes') !== r.sellable) return false;
      if (holdHdr && (holdHdr === 'yes') !== isHeld(r)) return false;
      if (menuHdr && (menuHdr === 'yes') !== (r.status !== 'off')) return false;
      if (sellableFilter === 'uber-changed' && !changedOnUber(r)) return false;
      if (changedOnly && !r.appPriceChanged) return false;
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
      switch (sortBy) {
        case 'price': return (a.priceCents - b.priceCents) * dir;
        case 'stock': return (((a.uberStock ?? a.stock) ?? -1) - ((b.uberStock ?? b.stock) ?? -1)) * dir;
        default: return String(a[sortBy]).localeCompare(String(b[sortBy])) * dir;
      }
    });
    return out;
  }, [rows, search, categoryFilter, subCategoryFilter, stockFilter, sellableFilter, sellableHdr, holdHdr, menuHdr, changedOnly, sortBy, sortDir]);

  const summary = useMemo(() => ({
    total: rows.length,
    inStock: rows.filter(onSaleOnUber).length,
    outOfStock: rows.filter(soldOutOnUber).length,
    suspended: rows.filter(isHeld).length,
    priceChanged: rows.filter((r) => r.appPriceChanged).length,
  }), [rows]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /** Keep an item on Uber or leave it off from the next push. */
  const setStatus = async (row: UberMenuRow, on: boolean) => {
    const status = on ? 'active' : 'off';
    setRows((rs) => rs.map((r) => (r.id === row.id && r.sku === row.sku ? { ...r, status } : r)));
    try {
      await authInstance.put(`admin/tools/uber-menu/catalogue/${encodeURIComponent(row.sku)}/status`, { status });
      notify(on ? `${row.name} goes on Uber at the next push` : `${row.name} comes off Uber at the next push`, { type: 'success' });
    } catch (err: any) {
      setRows((rs) => rs.map((r) => (r.id === row.id && r.sku === row.sku ? { ...r, status: row.status } : r)));
      notify(err?.response?.data?.error || 'Could not change it', { type: 'error' });
    }
  };

  /** Changed on Uber: take Uber's values into the catalogue, or put ours back on Uber. */
  const settleUber = async (targets: UberMenuRow[], take: 'uber' | 'ours') => {
    const list = targets.filter(changedOnUber);
    if (!list.length) return;
    if (list.length > 1 && !window.confirm(take === 'uber'
      ? `Copy what Uber shows for ${list.length} items into the catalogue (name, description, image, price, aisle)?`
      : `Put the catalogue's name, description, image and price back on Uber for ${list.length} items? (A different aisle goes back at the next push.)`)) return;
    try {
      const { data } = await authInstance.post('admin/tools/uber-menu/catalogue/uber-changes', { skus: list.map((r) => r.sku), take });
      const d = data?.data || {};
      if (take === 'uber') {
        notify(`Took Uber's values for ${d.saved ?? 0} item(s) into the catalogue`, { type: 'success' });
        await fetchMenu({ quiet: true });
      } else {
        const aisle: string[] = d.aisleAtNextPush || [];
        const keys = new Set(list.map((r) => r.sku));
        const aisleSet = new Set(aisle);
        // ours is on its way to Uber: only an aisle difference stays (it goes back at the next push)
        setRows((rs) => rs.map((r) => (keys.has(r.sku)
          ? { ...r, uberDiff: aisleSet.has(r.sku) ? (r.uberDiff || []).filter((x) => x.field === 'aisle') : [] }
          : r)));
        notify(`Putting ours back on Uber for ${(d.queued || []).length} item(s): showing in a minute or two${aisle.length ? `; ${aisle.length} aisle change(s) go back at the next push` : ''}`, { type: 'success' });
      }
    } catch (err: any) {
      notify(err?.response?.data?.error || 'Could not settle the Uber changes', { type: 'error' });
    }
  };

  /** Hold / release on Uber now (a minute or two to show). */
  const setHold = async (targets: UberMenuRow[], hold: boolean) => {
    const list = targets.filter((r) => r.sku && (r.onUber ?? true));
    if (!list.length) return;
    if (list.length > 1 && !window.confirm(`${hold ? 'Put' : 'Release'} ${list.length} item${list.length === 1 ? '' : 's'} ${hold ? 'on hold' : 'from hold'} on Uber? ${hold ? 'Nothing automated re-opens a held item.' : 'Each goes back on sale if it has stock, else it waits as sold out.'}`)) return;
    const keys = new Set(list.map((r) => r.sku));
    const apply = (held: boolean, base?: UberMenuRow[]) => setRows((rs) => rs.map((r) => {
      if (!keys.has(r.sku)) return r;
      const before = base?.find((b) => b.sku === r.sku);
      if (before) return before;
      const soldOut = r.stock != null && r.stock <= 0;
      return { ...r, held, sellable: held ? false : !soldOut, suspendedReason: held ? 'MANUAL_HOLD' : (soldOut ? 'OUT_OF_STOCK' : null) };
    }));
    const snapshot = rows.filter((r) => keys.has(r.sku));
    apply(hold);
    try {
      const { data } = await authInstance.post('admin/tools/uber-menu/hold', { skus: Array.from(keys), hold });
      const missing: string[] = data?.data?.notOnMenu || [];
      notify(`${hold ? 'On hold' : 'Released'}: ${(data?.data?.queued || []).length}, showing on Uber in a minute or two${missing.length ? ` (${missing.length} not on the Uber menu)` : ''}`, { type: 'success' });
    } catch (err: any) {
      apply(hold, snapshot);
      notify(err?.response?.data?.error || 'Could not change the hold', { type: 'error' });
    }
  };

  const handleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  };
  const sortArrow = (col: typeof sortBy) => (sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const hasFilters = !!(search || categoryFilter || subCategoryFilter || stockFilter !== 'all' || sellableFilter || sellableHdr || holdHdr || menuHdr || changedOnly);

  return (
    <Box sx={{ bgcolor: colors.bgDeepest, minHeight: '100vh', p: 3, overflowX: 'clip', maxWidth: '100%' }}>
      {/* Sticky header: title, refresh, summary cards and filters stay put while
          the table scrolls beneath them — same as Product Management. */}
      <Box ref={stickyHeaderRef} sx={{ position: 'sticky', top: APP_BAR_HEIGHT, zIndex: 10, bgcolor: colors.bgDeepest, pt: 1, width: '100%', minWidth: 0 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 800 }}>
              Uber Menu
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5 }}>
              What is on the Uber Eats menu right now
            </Typography>
          </Box>
        </Box>

        {/* The two "good" alerts (Jason, 26 Oct): green, side by side, settled in Push Uber Menu. */}
        {mode === 'catalogue' && !lsChecks && (
          <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, mb: 1.5 }}>
            New and mismatching products are checked on prod only (this environment's Lightspeed is the demo store).
          </Typography>
        )}
        {mode === 'catalogue' && lsChecks && (alerts ? (alerts.newProducts > 0 || alerts.mismatching > 0) && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {alerts.newProducts > 0 && (
              <Alert severity="success" variant="outlined" sx={{ flex: '1 1 280px', borderRadius: 2, color: colors.textPrimary }}>
                <strong>{alerts.newProducts.toLocaleString()} new product{alerts.newProducts === 1 ? '' : 's'}</strong> in Lightspeed for the Uber menu.
                Add or decline {alerts.newProducts === 1 ? 'it' : 'them'} in Uber Tools &rarr; Push Uber Menu.
              </Alert>
            )}
            {alerts.mismatching > 0 && (
              <Alert severity="success" variant="outlined" sx={{ flex: '1 1 280px', borderRadius: 2, color: colors.textPrimary }}>
                <strong>{alerts.mismatching.toLocaleString()} product{alerts.mismatching === 1 ? '' : 's'} mismatching</strong> Lightspeed (name, description, image, category or tags).
                Accept, keep or amend in Uber Tools &rarr; Push Uber Menu.
              </Alert>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, mb: 1.5 }}>
            <CircularProgress size={10} sx={{ mr: 1 }} />Checking Lightspeed for new and changed products...
          </Typography>
        ))}

        {mode === 'catalogue' && rows.some(changedOnUber) && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{ mb: 2, borderRadius: 2, color: colors.textPrimary }}
            action={(
              <Button size="small" color="warning" onClick={() => { setSellableFilter('uber-changed'); setPage(1); }} sx={{ textTransform: 'none' }}>
                Show them
              </Button>
            )}
          >
            <strong>{rows.filter(changedOnUber).length} item{rows.filter(changedOnUber).length === 1 ? '' : 's'} changed on Uber directly</strong> (name, description, image, price or aisle). A push would put ours back:
            take Uber's or keep ours on each one first.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {cachedAt && (
                <Typography sx={{ fontSize: '0.7rem', color: colors.textMuted, fontFamily: 'monospace' }}>
                  menu read {new Date(cachedAt).toLocaleString()}
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing || loading}
                sx={{ borderColor: colors.border, color: colors.textSecondary, textTransform: 'none' }}
              >
                {refreshing ? 'Refreshing...' : 'Refresh from Uber'}
              </Button>
              {mode === 'live' && !loading && rows.length > 0 && (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={seeding ? <CircularProgress size={14} /> : <SeedIcon />}
                    onClick={handleSeed}
                    disabled={seeding || loading}
                    sx={{ borderColor: colors.border, color: colors.textSecondary, textTransform: 'none' }}
                  >
                    Set up the Uber catalogue (copy the live menu in)
                  </Button>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* ── Summary Cards ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard icon={<StorefrontIcon />} label={mode === 'catalogue' ? 'Items in the Uber catalogue' : 'Items on Uber'} value={summary.total} color="#06C167" colors={colors} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard icon={<InStockIcon />} label="On sale on Uber" value={summary.inStock} color={colors.success} colors={colors} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard icon={<OutOfStockIcon />} label="Sold out on Uber" value={summary.outOfStock} color={colors.error} colors={colors} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <GlowCard icon={<SuspendedIcon />} label="On hold" value={summary.suspended} color={colors.warning} colors={colors} />
          </Grid>
        </Grid>

        {/* ── Filter Bar ── */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" useFlexGap>
            <TextField
              size="small"
              placeholder="Search name or SKU..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: colors.textMuted, fontSize: 20 }} /></InputAdornment>,
              }}
              sx={{
                minWidth: 280,
                '& .MuiInputBase-root': { bgcolor: colors.bgElevated, color: colors.textPrimary },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
              }}
            />
            <FilterAutocomplete label="Category" placeholder="All Categories" options={categories} value={categoryFilter} onChange={(v) => { setCategoryFilter(v); setPage(1); }} colors={colors} />
            <FilterAutocomplete label="Sub Category" placeholder="All Sub Categories" options={subCategories} value={subCategoryFilter} onChange={(v) => { setSubCategoryFilter(v); setPage(1); }} colors={colors} />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: colors.textSecondary }}>Stock</InputLabel>
              <Select value={stockFilter} onChange={(e) => { setStockFilter(e.target.value as string); setPage(1); }} label="Stock" sx={selectSx(colors)}>
                <MenuItem value="all">All Stock</MenuItem>
                <MenuItem value="in-stock">On sale on Uber</MenuItem>
                <MenuItem value="out-of-stock">Sold out on Uber</MenuItem>
                <MenuItem value="low-stock">Low Stock (&le;5)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: colors.textSecondary }}>Sellable</InputLabel>
              <Select value={sellableFilter} onChange={(e) => { setSellableFilter(e.target.value as string); setPage(1); }} label="Sellable" sx={selectSx(colors)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Sellable</MenuItem>
                <MenuItem value="false">Suspended</MenuItem>
                <MenuItem value="held">On hold</MenuItem>
                {mode === 'catalogue' && <MenuItem value="uber-changed">Changed on Uber</MenuItem>}
              </Select>
            </FormControl>
            <Chip
              label={`${mode === 'catalogue' ? 'Lightspeed' : 'App'} price changed (${summary.priceChanged})`}
              onClick={() => { setChangedOnly((v) => !v); setPage(1); }}
              color="warning"
              variant={changedOnly ? 'filled' : 'outlined'}
              size="small"
            />
            {hasFilters && (
              <Button
                size="small"
                onClick={() => { setSearch(''); setCategoryFilter(''); setSubCategoryFilter(''); setStockFilter('all'); setSellableFilter(''); setSellableHdr(''); setHoldHdr(''); setMenuHdr(''); setChangedOnly(false); setPage(1); }}
                sx={{ color: colors.textSecondary, textTransform: 'none' }}
              >
                Clear Filters
              </Button>
            )}
            {sellableFilter === 'uber-changed' && filtered.length > 0 && (
              <>
                <Button size="small" variant="outlined" color="warning" onClick={() => settleUber(filtered, 'uber')} sx={{ textTransform: 'none' }}>
                  Take Uber's for {filtered.length} shown
                </Button>
                <Button size="small" variant="outlined" onClick={() => settleUber(filtered, 'ours')} sx={{ textTransform: 'none' }}>
                  Keep ours for {filtered.length} shown
                </Button>
              </>
            )}
            {hasFilters && filtered.length > 0 && (
              <>
                <Button size="small" variant="outlined" color="warning" onClick={() => setHold(filtered.filter((r) => (r.onUber ?? true) && !isHeld(r)), true)} sx={{ textTransform: 'none' }}>
                  Hold {filtered.filter((r) => (r.onUber ?? true) && !isHeld(r)).length} shown
                </Button>
                <Button size="small" variant="outlined" onClick={() => setHold(filtered.filter((r) => (r.onUber ?? true) && isHeld(r)), false)} sx={{ textTransform: 'none' }}>
                  Release {filtered.filter((r) => (r.onUber ?? true) && isHeld(r)).length} shown
                </Button>
              </>
            )}
            <Typography sx={{ ml: 'auto', fontSize: '0.75rem', color: colors.textMuted, fontFamily: 'monospace' }}>
              {filtered.length.toLocaleString()} of {rows.length.toLocaleString()}
            </Typography>
          </Stack>
        </Paper>
      </Box>

      {/* ── Table ── */}
      <TableContainer
        ref={listTopRef}
        component={Paper}
        sx={{
          bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2,
          maxWidth: '100%', overflowX: 'auto', maxHeight: 'calc(100vh - 300px)', minHeight: 320,
          '& .MuiTableHead-root .MuiTableCell-root': { bgcolor: colors.bgCard },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx(colors), width: 50 }}>Image</TableCell>
              {([
                { key: 'sku', label: 'SKU' },
                { key: 'name', label: 'Name' },
                { key: 'category', label: 'Category' },
                { key: 'subCategory', label: 'Sub Category' },
                { key: 'price', label: 'Price' },
                { key: 'stock', label: 'Stock' },
              ] as Array<{ key: typeof sortBy; label: string }>).map((col) => (
                <TableCell
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  sx={{
                    ...headCellSx(colors),
                    color: sortBy === col.key ? colors.info : colors.textMuted,
                    cursor: 'pointer', '&:hover': { color: colors.info },
                    ...(col.key === 'name' ? { minWidth: 200 } : {}),
                  }}
                >
                  {col.label}{sortArrow(col.key)}
                </TableCell>
              ))}
              {([
                { label: 'Sellable', value: sellableHdr, set: setSellableHdr, show: true, tip: 'Click: only sellable, then only not sellable, then all' },
                { label: 'Hold', value: holdHdr, set: setHoldHdr, show: true, tip: 'Click: only on hold, then only not on hold, then all' },
                { label: 'On menu', value: menuHdr, set: setMenuHdr, show: mode === 'catalogue', tip: 'Click: only switched on, then only switched off, then all' },
              ] as Array<{ label: string; value: YesNo; set: (v: YesNo) => void; show: boolean; tip: string }>).filter((h) => h.show).map((h) => (
                <TableCell
                  key={h.label}
                  role="button"
                  tabIndex={0}
                  title={h.tip}
                  aria-label={`Filter ${h.label}${h.value ? `: ${h.value}` : ''}`}
                  onClick={() => { h.set(cycle(h.value)); setPage(1); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); h.set(cycle(h.value)); setPage(1); } }}
                  sx={{ ...headCellSx(colors), cursor: 'pointer', userSelect: 'none', color: h.value ? colors.info : colors.textMuted, '&:hover': { color: colors.info } }}
                >
                  {h.label}{h.value ? `: ${h.value === 'yes' ? 'Yes' : 'No'}` : ''}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: mode === 'catalogue' ? 10 : 9 }).map((_, j) => (
                    <TableCell key={j} sx={{ borderColor: colors.border }}>
                      <Skeleton sx={{ bgcolor: colors.bgElevated }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : pageRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={mode === 'catalogue' ? 10 : 9} sx={{ textAlign: 'center', py: 6, color: colors.textMuted, borderColor: colors.border }}>
                  {rows.length === 0 ? 'Nothing on the Uber menu' : 'No items match these filters'}
                </TableCell>
              </TableRow>
            ) : (
              pageRows.map((row) => (
                <TableRow key={`${row.sku}|${row.id}`} sx={{ '&:hover': { bgcolor: colors.bgElevated } }}>
                  <TableCell sx={{ borderColor: colors.border, py: 0.5 }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 1, overflow: 'hidden', bgcolor: colors.bgElevated,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {row.imageUrl ? (
                        <Box component="img" src={row.imageUrl} loading="lazy" decoding="async" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <StorefrontIcon sx={{ fontSize: 20, color: colors.textMuted }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderColor: colors.border }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: colors.textPrimary }}>{row.sku || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: colors.border, maxWidth: 260 }}>
                    <Typography noWrap sx={{ fontSize: '0.85rem', color: colors.textPrimary }}>{row.name}</Typography>
                    {changedOnUber(row) && (
                      <Box sx={{ mt: 0.25 }}>
                        <Typography sx={{ fontSize: '0.68rem', color: colors.warning, whiteSpace: 'normal' }}>
                          Changed on Uber: {row.uberDiff!.map((d) => (d.field === 'image' || d.field === 'description'
                            ? DIFF_LABEL[d.field]
                            : `${DIFF_LABEL[d.field] ?? d.field} ${d.uber || '(none)'} (ours ${d.ours || '(none)'})`)).join(' · ')}
                        </Typography>
                        <Button size="small" onClick={() => settleUber([row], 'uber')} sx={{ minWidth: 0, p: '0 4px', fontSize: '0.68rem', textTransform: 'none' }}>Take Uber's</Button>
                        <Button size="small" onClick={() => settleUber([row], 'ours')} sx={{ minWidth: 0, p: '0 4px', fontSize: '0.68rem', textTransform: 'none' }}>Keep ours</Button>
                      </Box>
                    )}
                    {row.mismatch && row.mismatch.length > 0 && (
                      <Typography sx={{ fontSize: '0.68rem', color: colors.success }}>
                        Lightspeed changed: {row.mismatch.map((m) => (m === 'gone' ? 'no longer in Lightspeed' : m)).join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ borderColor: colors.border }}>
                    {row.category ? (
                      <Chip label={row.category} size="small" sx={{ bgcolor: alpha(colors.info, 0.1), color: colors.info, fontSize: '0.7rem', height: 22 }} />
                    ) : (
                      <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ borderColor: colors.border }}>
                    {row.subCategory ? (
                      <Chip label={row.subCategory} size="small" sx={{ bgcolor: alpha(colors.purple, 0.1), color: colors.purple, fontSize: '0.7rem', height: 22 }} />
                    ) : (
                      <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ borderColor: colors.border, minWidth: 150 }}>
                    <PriceCell row={row} colors={colors} editable={mode === 'catalogue' || !!row.productId} priceLabel={mode === 'catalogue' ? 'Lightspeed' : 'App'} onSave={(c) => savePrice(row, c)} />
                  </TableCell>
                  <TableCell sx={{ borderColor: colors.border }}>
                    <Typography
                      title={row.stock != null ? `App stock at Diep River: ${row.stock}` : 'Stock as Uber holds it (from Vend through the stock sync)'}
                      sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace', color: getStockColor(row.uberStock ?? row.stock, colors) }}
                    >
                      {row.uberStock != null ? `${row.uberStock}${row.uberStockCapped ? '+' : ''}` : (row.stock ?? '—')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: colors.border }}>
                    <YesNoChip on={row.sellable} colors={colors} />
                  </TableCell>
                  <TableCell sx={{ borderColor: colors.border }}>
                    {(row.onUber ?? true) ? (
                      <Switch
                        size="small"
                        color="warning"
                        checked={isHeld(row)}
                        onChange={(e) => setHold([row], e.target.checked)}
                        slotProps={{ input: { 'aria-label': `${row.name} on hold` } }}
                      />
                    ) : null}
                  </TableCell>
                  {mode === 'catalogue' && (
                    <TableCell sx={{ borderColor: colors.border, whiteSpace: 'nowrap' }}>
                      <Switch
                        size="small"
                        checked={row.status !== 'off'}
                        onChange={(e) => setStatus(row, e.target.checked)}
                        slotProps={{ input: { 'aria-label': `${row.name} on the Uber menu` } }}
                      />
                      {row.status === 'off' && row.onUber && (
                        <Typography component="span" sx={{ fontSize: '0.68rem', color: colors.warning }}>off at next push</Typography>
                      )}
                      {row.status !== 'off' && !row.onUber && (
                        <Typography component="span" sx={{ fontSize: '0.68rem', color: colors.textMuted }}>on at next push</Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            sx={{ '& .MuiPaginationItem-root': { color: colors.textSecondary, '&.Mui-selected': { bgcolor: alpha(colors.info, 0.2), color: colors.info } } }}
          />
        </Box>
      )}
    </Box>
  );
};

export default UberMenu;
